import * as React from "react";
import { cn } from "../../lib/utils";

// Типы для конфигурации графика
export const chartConfig = {
  colors: {
    posTop10: "#2E7D32", // темно-зеленый
    posTop20: "#c4f7c9", // светло-зеленый
    negTop10: "#C62828", // темно-красный
    negTop20: "#ffcaca", // светло-красный
  },
};

// Контейнер для графика
const ChartContainer = React.forwardRef(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn("h-[350px] w-full", className)}
        {...props}
      >
        {children}
      </div>
    );
  }
);
ChartContainer.displayName = "ChartContainer";

// Компонент для всплывающей подсказки
const ChartTooltip = ({ className, ...props }) => (
  <div
    className={cn(
      "rounded-md border bg-white p-2 shadow-md dark:bg-gray-950 dark:text-gray-50",
      className
    )}
    {...props}
  />
);
ChartTooltip.displayName = "ChartTooltip";

// Содержимое всплывающей подсказки
const ChartTooltipContent = React.forwardRef(
  ({ className, payload, label, formatter, ...props }, ref) => {
    const formattedLabel = React.useMemo(() => {
      if (!label) return null;
      
      // Форматирование даты
      if (label instanceof Date) {
        return formatDate(label);
      }
      
      // Если строка уже в формате dd.mm.yyyy — возвращаем как есть
      if (typeof label === "string" && /^\d{2}\.\d{2}\.\d{4}$/.test(label)) {
        return label;
      }
      
      // Если это ISO-строка даты, преобразуем в объект Date и форматируем
      if (typeof label === "string" && /^\d{4}-\d{2}-\d{2}/.test(label)) {
        return formatDate(new Date(label));
      }
      
      return label;
    }, [label]);

    const formattedValue = React.useCallback(
      (value, name) => {
        if (formatter) {
          return formatter(value, name);
        }
        return value;
      },
      [formatter]
    );

    if (!payload?.length) {
      return null;
    }

    return (
      <ChartTooltip>
        <div
          ref={ref}
          className={cn("space-y-2", className)}
          {...props}
        >
          <div className="text-sm font-medium">{formattedLabel}</div>
          <div className="space-y-1">
            {payload.map(({ value, name, color }, index) => (
              <div
                key={`item-${index}`}
                className="flex items-center justify-between gap-2 text-xs"
              >
                <div className="flex items-center gap-1">
                  <div
                    className="h-2 w-2 rounded-full"
                    style={{ backgroundColor: color }}
                  />
                  <span>{name}</span>
                </div>
                <span className="font-medium">{formattedValue(value, name)}</span>
              </div>
            ))}
          </div>
        </div>
      </ChartTooltip>
    );
  }
);
ChartTooltipContent.displayName = "ChartTooltipContent";

// Вспомогательная функция для форматирования даты
function formatDate(date) {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  const pad = n => n.toString().padStart(2, "0");
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
}

export { ChartContainer, ChartTooltip, ChartTooltipContent };
